/**
 * Problem Detail Page
 * Full code editor with test case runner
 */
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { problemService, codeService } from '../services/api';
import CodeEditor from '../components/problem/CodeEditor';
import TestResults from '../components/problem/TestResults';
import { useRecentlyViewed } from '../hooks';
import './ProblemDetailPage.css';

const TABS = [
  { id: 'description', label: '📋 Problem' },
  { id: 'hints', label: '💡 Hints' },
  { id: 'testcases', label: '🧪 Test Cases' },
];

function ProblemDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [problem, setProblem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [code, setCode] = useState('');
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [runError, setRunError] = useState(null);
  const [activeTab, setActiveTab] = useState('description');
  const [showHints, setShowHints] = useState([]);
  const { addRecent } = useRecentlyViewed();

  useEffect(() => {
    problemService.getById(id)
      .then(res => {
        setProblem(res.data);
        setCode(res.data.starterCode || '');
        addRecent({ id: res.data.id, name: res.data.title, type: 'problem' });
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  const handleRun = async () => {
    if (!code.trim()) return;
    setRunning(true);
    setRunError(null);
    setResults(null);

    try {
      const res = await codeService.run(code, id);
      setResults(res.data);
    } catch (err) {
      setRunError(err.message);
    } finally {
      setRunning(false);
    }
  };

  const handleRevealHint = (idx) => {
    if (!showHints.includes(idx)) {
      setShowHints(prev => [...prev, idx]);
    }
  };

  const handleReset = () => {
    if (window.confirm('Reset code to starter template?')) {
      setCode(problem.starterCode || '');
      setResults(null);
      setRunError(null);
    }
  };

  if (loading) return <div className="loader" style={{ height: '60vh' }}><div className="spinner" /></div>;
  if (error) return (
    <div className="container" style={{ padding: '60px 24px', textAlign: 'center' }}>
      <p>⚠️ {error}</p>
      <button className="btn btn-secondary" onClick={() => navigate('/problems')} style={{ marginTop: 16 }}>
        Back to Problems
      </button>
    </div>
  );

  const diffColor = { Easy: 'badge-easy', Medium: 'badge-medium', Hard: 'badge-hard' };

  return (
    <div className="problem-detail-page">
      <div className="problem-layout">
        {/* ── LEFT PANEL: Problem Info ───────────────────────────────── */}
        <div className="problem-left">
          {/* Breadcrumb */}
          <div className="breadcrumb">
            <Link to="/problems">Problems</Link>
            <span>›</span>
            <span>{problem.title}</span>
          </div>

          {/* Title */}
          <div className="problem-header">
            <div className="problem-badges">
              <span className={`badge ${diffColor[problem.difficulty]}`}>{problem.difficulty}</span>
              <span className="badge badge-accent">{problem.category}</span>
              <span className="problem-pts">🏆 {problem.points} pts</span>
            </div>
            <h1>{problem.title}</h1>
            {problem.algorithmId && (
              <Link to={`/algorithms/${problem.algorithmId}`} className="algo-link">
                📚 Study the algorithm first →
              </Link>
            )}
          </div>

          {/* Left Tabs */}
          <div className="tabs">
            {TABS.map(tab => (
              <button
                key={tab.id}
                className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="problem-tab-content">
            {/* Description */}
            {activeTab === 'description' && (
              <div className="desc-tab">
                <div className="desc-section">
                  <h4>Problem Statement</h4>
                  <p>{problem.description}</p>
                </div>

                <div className="desc-section">
                  <h4>Input Format</h4>
                  <pre>{problem.inputFormat}</pre>
                </div>

                <div className="desc-section">
                  <h4>Output Format</h4>
                  <pre>{problem.outputFormat}</pre>
                </div>

                {/* Examples */}
                {problem.examples?.map((ex, i) => (
                  <div key={i} className="example-block">
                    <h4>Example {i + 1}</h4>
                    <div className="example-io">
                      <div className="io-block">
                        <span className="io-tag">Input</span>
                        <pre>{ex.input}</pre>
                      </div>
                      <div className="io-block">
                        <span className="io-tag">Output</span>
                        <pre>{ex.output}</pre>
                      </div>
                    </div>
                    {ex.explanation && (
                      <div className="example-explanation">
                        <span>💡</span>
                        <p>{ex.explanation}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Hints */}
            {activeTab === 'hints' && (
              <div className="hints-tab">
                <p className="hints-intro">Click to reveal hints one by one. Try to solve without them first!</p>
                <div className="hints-list">
                  {problem.hints?.map((hint, i) => (
                    <div key={i} className="hint-item">
                      <div className="hint-header">
                        <span className="hint-num">Hint {i + 1}</span>
                        {!showHints.includes(i) && (
                          <button className="btn btn-ghost btn-sm" onClick={() => handleRevealHint(i)}>
                            Reveal →
                          </button>
                        )}
                      </div>
                      {showHints.includes(i) && (
                        <div className="hint-text">
                          <p>{hint}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Test Cases */}
            {activeTab === 'testcases' && (
              <div className="testcases-tab">
                <p className="text-muted" style={{ fontSize: 13, marginBottom: 16 }}>
                  Public test cases shown. Hidden test cases run when you submit.
                </p>
                {problem.testCases?.filter(tc => tc.isPublic).map((tc, i) => (
                  <div key={tc.id} className="tc-preview">
                    <div className="tc-header">
                      <span>Test {i + 1}</span>
                      <span className="badge badge-accent">Public</span>
                    </div>
                    <div className="tc-io">
                      <div className="io-block">
                        <span className="io-tag">Input</span>
                        <pre>{tc.input}</pre>
                      </div>
                      <div className="io-block">
                        <span className="io-tag">Expected Output</span>
                        <pre>{tc.expectedOutput}</pre>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="hidden-tests-note">
                  <span>🔒</span>
                  <p>{problem.testCases?.filter(tc => !tc.isPublic).length || 0} additional hidden test cases</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT PANEL: Code Editor ───────────────────────────────── */}
        <div className="problem-right">
          {/* Editor Header */}
          <div className="editor-header">
            <div className="editor-lang">
              <span className="lang-dot" />
              JavaScript
            </div>
            <div className="editor-actions">
              <button className="btn btn-ghost btn-sm" onClick={handleReset}>
                ↺ Reset
              </button>
              <button
                className="btn btn-primary"
                onClick={handleRun}
                disabled={running}
                style={{ minWidth: 100 }}
              >
                {running ? (
                  <>
                    <span className="run-spinner" />
                    Running...
                  </>
                ) : '▶ Run Code'}
              </button>
            </div>
          </div>

          {/* Code Editor */}
          <CodeEditor
            value={code}
            onChange={setCode}
            height="calc(100vh - 380px)"
          />

          {/* Results Area */}
          <div className="results-area">
            {runError && (
              <div className="run-error">
                <span>⚠️</span>
                <p>{runError}</p>
              </div>
            )}
            {results && <TestResults results={results} />}
            {!results && !runError && (
              <div className="run-placeholder">
                <p>Press <strong>Run Code</strong> to test your solution against all test cases</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProblemDetailPage;
